import { useState, useMemo, useCallback, ReactNode } from 'react'
import { Snackbar, Alert } from '@mui/material'
import {
  NotificationContext,
  NotificationContextType,
} from '../contexts/NotificationContext'
import { NotificationSeverity } from '../constants/notification'
import { useTheme } from '../contexts/ThemeContext'
import { NOTIFICATION_AUTO_HIDE_DURATION } from '@/constants/main'

interface Notification {
  message: string
  severity: NotificationSeverity
}

export default function NotificationProvider({
  children,
}: {
  children: ReactNode
}) {
  const [notification, setNotification] = useState<Notification | null>(null)
  const { colors } = useTheme()

  const getSeverityColor = useCallback(
    (severity: NotificationSeverity): string => {
      switch (severity) {
        case NotificationSeverity.Success:
          return colors.primary
        case NotificationSeverity.Error:
          return colors.secondary
        case NotificationSeverity.Warning:
          return colors.warning
        default:
          return colors.primary
      }
    },
    [colors]
  )

  const showNotification = useCallback(
    (message: string, severity: NotificationSeverity) => {
      setNotification({ message, severity })
    },
    []
  )

  const handleClose = useCallback(() => {
    setNotification(null)
  }, [])

  const contextValue = useMemo<NotificationContextType>(
    () => ({
      showError: (message: string) =>
        showNotification(message, NotificationSeverity.Error),
      showSuccess: (message: string) =>
        showNotification(message, NotificationSeverity.Success),
      showWarning: (message: string) =>
        showNotification(message, NotificationSeverity.Warning),
    }),
    [showNotification]
  )

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={!!notification}
        autoHideDuration={NOTIFICATION_AUTO_HIDE_DURATION}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 2 }}
      >
        <Alert
          onClose={handleClose}
          severity={notification?.severity || NotificationSeverity.Info}
          variant="outlined"
          sx={{
            width: '100%',
            backgroundColor: colors.overlay,
            border: `2px solid ${
              notification
                ? getSeverityColor(notification.severity)
                : colors.primary
            }`,
            boxShadow: `0 0 20px ${colors.primaryShadow}`,
            color: colors.text,
            fontFamily: "'TWKEverett', sans-serif",
            fontWeight: 500,
            '& .MuiAlert-icon': {
              color: notification
                ? getSeverityColor(notification.severity)
                : colors.primary,
            },
            '& .MuiAlert-action': {
              color: colors.text,
              '& .MuiIconButton-root': {
                color: colors.text,
                '&:hover': {
                  backgroundColor: `${colors.primary}22`,
                },
              },
            },
          }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  )
}
